'use client';

import { memo, useCallback, useMemo } from 'react';

import Link from 'next/link';

import BackgroundGradient from '@/components/elements/background-gradient';
import IconText2 from '@/components/elements/icon/icon-text2';
import useAmplitudeContext from '@/hooks/useAmplitudeContext';
import { useTranslation } from '@/i18n/client';
import { EventCardBase } from '@/types/calendar/events';
import { WithLang } from '@/types/general';
import { amplitudeEvents } from '@/utils/constants';
import { getEventDates } from '@/utils/date-utils';
import { AssetKey } from '@/utils/image-load-utils';
import { buildUrl } from '@/utils/navigation';

import ImageAsset from '../elements/image-asset';

const EventCardPromoted = memo(
    (
        props: WithLang<{
            event: EventCardBase;
            priority: boolean;
            assetKey: AssetKey;
        }>,
    ) => {
        const { startDate, endDate } = getEventDates()(props.event.start_date, props.event.duration);
        const { trackAmplitudeEvent } = useAmplitudeContext();

        const { t: tCa } = useTranslation(props.lang, 'calendar');
        const { t: tCo } = useTranslation(props.lang, 'common');

        const dateStrings = useMemo(() => {
            return {
                dateString: tCa('formats.dayMonthWeekday', {
                    day: startDate.date(),
                    month: startDate.month(),
                    weekday: startDate.day(),
                }),
                time:
                    `${startDate.hour()}:${startDate.minute().toString().padStart(2, '0')} – ` +
                    `${endDate.hour()}:${endDate.minute().toString().padStart(2, '0')}`,
            };
        }, [startDate, endDate, tCa]);

        const handleClick = useCallback(() => {
            trackAmplitudeEvent(amplitudeEvents.EVENT_PROMOTED_OPEN, {
                eventId: props.event.id,
                eventName: props.event.name,
            });
        }, [trackAmplitudeEvent, props.event.id, props.event.name]);

        return (
            <div className="relative h-full overflow-hidden rounded-lg">
                <Link
                    onClick={handleClick}
                    aria-label={`link to event ${props.event.name}`}
                    className="cursor-pointer"
                    href={buildUrl('events', { lang: props.lang, children: [props.event.slug || props.event.id] })}
                    scroll={false}
                >
                    <BackgroundGradient
                        isAnimated={props.event.animated!}
                        filenameDisk={props.event.image_cover?.filename_disk ?? ''}
                        ids={[props.event.image_cover?.id]}
                    />
                    <div className="mx-[5%] flex h-full justify-between md:ml-[5%]">
                        <div className="relative z-[1] my-4 flex flex-1 flex-col gap-y-4 text-white">
                            <div className="flex self-start rounded bg-white bg-opacity-20 p-2">
                                <IconText2
                                    icon="phi-thumbs-up"
                                    text={tCo('common.recommended')}
                                    sizeSm={4}
                                    sizeMd={6}
                                    textClass="text-xs lg:text-lg"
                                    iconClass="fill-white"
                                />
                            </div>
                            <p
                                className={`font-bold md:text-lg xl:text-xl xxl:text-2xl ${
                                    props.event.name?.length > 50 ? 'text-[10px]' : 'text-sm'
                                }`}
                            >
                                {props.event.name}
                            </p>
                            {dateStrings.dateString && (
                                <IconText2
                                    icon="phi-calendar-16"
                                    text={dateStrings.dateString}
                                    sizeMd={5}
                                    textClass="text-xs"
                                />
                            )}
                            {dateStrings.time && (
                                <IconText2 icon="phi-clock-16" text={dateStrings.time} sizeMd={5} textClass="text-xs" />
                            )}
                        </div>
                        <div className="relative flex-1">
                            <ImageAsset
                                isAnimated={props.event.animated!}
                                filenameDisk={props.event.image_cover?.filename_disk ?? ''}
                                objectFit="contain"
                                priority={props.priority}
                                ids={[props.event.image_cover?.id]}
                                assetKey={props.assetKey}
                            />
                        </div>
                    </div>
                </Link>
            </div>
        );
    },
);

EventCardPromoted.displayName = 'EventCardPromoted';

export default EventCardPromoted;
